if (!customElements.get('shipping-calculator')) {
class ShippingCalculator extends HTMLElement {
  constructor() {
    super();

    this.needEndLoading = false;
    this.showingErrors = false;
    this.generatedShipping = 0;
    this.button = this.querySelector('button');
    this.addressCountrySelect = this.querySelector('[name="address[country]"]');
    this.addressProvinceSelect = this.querySelector('[name="address[province]"]');
    this.addressZipInput = this.querySelector('[name="address[zip]"]');
    this.resultsTag = this.querySelector('.shipping-calculator__results');
    this.resultsList = this.querySelector('.shipping-calculator__results-list');
    this.resultsErrorHeading = this.querySelector('.shipping-calculator__error-heading');
    this.resultsSuccessHeading = this.querySelector('.shipping-calculator__success-heading');
    this.countryIsoMap = this.parseCountryIsoMap();

    this.button.addEventListener('click', this.onButtonClick.bind(this));
    this.addEventListener('change', this.hideErrorsTag.bind(this), false);

    this.onCartChangeHandler = this.onCartChange.bind(this);
    document.addEventListener('afterCartChanged', this.onCartChangeHandler);
    document.addEventListener('afterUpdateQuantity', this.onCartChangeHandler);
  }

  connectedCallback() {
    this.setupCountries();
  }

  disconnectedCallback() {
    document.removeEventListener('afterCartChanged', this.onCartChangeHandler);
    document.removeEventListener('afterUpdateQuantity', this.onCartChangeHandler);
  }

  parseCountryIsoMap() {
    const fallback = { Australia: 'AU', 'New Zealand': 'NZ' };
    try {
      const parsed = JSON.parse(this.querySelector('[data-shipping-country-iso]')?.textContent || '{}');
      return { ...fallback, ...parsed };
    } catch (e) {
      return fallback;
    }
  }

  setupCountries() {
    if (Shopify && Shopify.CountryProvinceSelector && this.addressCountrySelect?.id && this.addressProvinceSelect?.id) {
      new Shopify.CountryProvinceSelector(this.addressCountrySelect.id, this.addressProvinceSelect.id, {
        hideElement: this.querySelector('[data-province-container]')?.id
      });
    }
  }

  onCartChange() {
    this.resultsTag.classList.add('hidden');
  }

  serialize(obj, prefix) {
    var str = [], p;
    for (p in obj) {
      if (obj.hasOwnProperty(p)) {
        var k = prefix ? prefix + "[" + p + "]" : p,
          v = obj[p];
        if (v === '' || v === null || v === undefined) continue;
        str.push((typeof v === "object") ?
          this.serialize(v, k) :
          encodeURIComponent(k) + "=" + encodeURIComponent(v));
      }
    }
    return str.join("&");
  }

  getAddress() {
    const country = (this.addressCountrySelect?.value || '').trim();
    const province = (this.addressProvinceSelect?.value || '').trim();
    const raw = (this.addressZipInput?.value || '').trim();
    const postcodeMatch = raw.match(/\b\d{4,5}(?:-\d{4})?\b/);
    const zip = postcodeMatch ? postcodeMatch[0] : raw;
    const city = raw.replace(postcodeMatch?.[0] || '', '').replace(/[,\s]+/g, ' ').trim() || zip;
    return {
      country,
      province,
      zip,
      city,
      address1: city || zip || 'Shipping'
    };
  }

  getShippingQuery(includeCarrierRates = true) {
    const address = this.getAddress();
    const shipping_address = {
      country: address.country,
      zip: address.zip,
      city: address.city,
      address1: address.address1
    };
    if (address.province) shipping_address.province = address.province;

    const params = { shipping_address };
    if (includeCarrierRates) params.include_carrier_rates = true;
    return this.serialize(params);
  }

  countryIso(countryName) {
    if (!countryName) return '';
    if (countryName.length === 2) return countryName.toUpperCase();
    return this.countryIsoMap[countryName] || '';
  }

  provinceCode(countryIso, province) {
    if (!province) return '';
    if (province.length <= 3) return province.toUpperCase();
    const map = {
      AU: {
        'Australian Capital Territory': 'ACT',
        'New South Wales': 'NSW',
        'Northern Territory': 'NT',
        'Queensland': 'QLD',
        'South Australia': 'SA',
        'Tasmania': 'TAS',
        'Victoria': 'VIC',
        'Western Australia': 'WA'
      }
    };
    return map[countryIso]?.[province] || province;
  }

  cartFetch(url, method = 'GET') {
    return fetch(url, {
      method,
      cache: 'no-store',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
  }

  async parseJsonOrEmpty(response) {
    const text = await response.text();
    if (!text || text === 'null') return null;
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  }

  hasShippingRates(data) {
    return Array.isArray(data?.shipping_rates) && data.shipping_rates.length > 0;
  }

  startLoading() {
    this.needEndLoading = false;
    this.button.setAttribute('aria-disabled', true);
    this.button.classList.add('loading');
    this.querySelector('.loading-overlay__spinner').classList.remove('hidden');
  }

  endLoading() {
    if (this.needEndLoading) {
      this.needEndLoading = false;
      this.button.setAttribute('aria-disabled', false);
      this.button.classList.remove('loading');
      this.querySelector('.loading-overlay__spinner').classList.add('hidden');
    }
  }

  appendResultNode(text) {
    const li = document.createElement("li");
    const node = document.createTextNode(text);
    li.appendChild(node);
    this.resultsList.appendChild(li);
  }

  handleResultsList(list) {
    this.resultsList.innerHTML = '';
    if (list?.shipping_rates?.length == 0) {
      this.resultsSuccessHeading.classList.add("hidden");
      this.resultsErrorHeading.classList.add("hidden");
      this.appendResultNode(this.dataset.checkoutRatesMessage || this.dataset.doNotShipMessage);
      this.resultsTag.classList.remove("hidden");
      return;
    }
    for (var key in list) {
      if (Array.isArray(list[key])) {
        list[key].forEach((item) => {
          if (typeof item === 'object') {
            const li = document.createElement("li");
            const spanPrice = document.createElement("span");
            spanPrice.classList.add('price');
            const span = document.createElement("span");
            span.classList.add('cart-item__price', 'heading-font');
            const text = item.presentment_name + ': ';
            span.innerHTML = Shopify.showPrice(Math.round(parseFloat(item.price) * 100));
            spanPrice.append(span);
            const node = document.createTextNode(text);
            li.appendChild(node);
            li.appendChild(spanPrice);
            if (item?.delivery_days && item.delivery_days.length > 0) {
              const spanDate = document.createElement("span");
              spanDate.classList.add('caption', 'shipping-calculator__date');
              var spanDateText = '( ';
              if (item.delivery_days[0] == item.delivery_days[1]) {
                if (parseInt(item.delivery_days[0]) <= 1) {
                  spanDateText += this.dataset.deliveryDateOne.replace('[count]', item.delivery_days[0]);
                } else {
                  spanDateText += this.dataset.deliveryDateMany.replace('[count]', item.delivery_days[0]);
                }
              } else {
                spanDateText += this.dataset.deliveryDateRange.replace('[minimum]', item.delivery_days[0]).replace('[maximum]', item.delivery_days[1]);
              }
              spanDateText += ' )';
              const nodeSpanDateText = document.createTextNode(spanDateText);
              spanDate.appendChild(nodeSpanDateText);
              li.appendChild(spanDate);
            }
            this.resultsList.appendChild(li);
          } else {
            this.appendResultNode(item);
          }
        });
      } else {
        this.appendResultNode(list[key]);
      }
    }
    this.resultsTag.classList.remove('hidden');
  }

  handleErrorResponse(errors) {
    this.generatedShipping = 0;
    this.showingErrors = true;
    this.resultsErrorHeading.classList.remove('hidden');
    this.resultsSuccessHeading.classList.add('hidden');
    this.handleResultsList(errors);
  }

  handleSuccessResponse(list) {
    this.generatedShipping = 0;
    this.showingErrors = false;
    this.resultsSuccessHeading.classList.remove('hidden');
    this.resultsErrorHeading.classList.add('hidden');
    this.handleResultsList(list);
  }

  hideErrorsTag() {
    if (this.showingErrors) {
      this.resultsTag.classList.add('hidden');
      this.showingErrors = false;
    }
  }

  onButtonClick() {
    if (this.button.classList.contains('loading')) return;

    this.generatedShipping = 0;
    this.startLoading();
    this.prepareAndPoll();
  }

  prepareAndPoll() {
    const query = this.getShippingQuery(true);

    this.cartFetch(`${routes.cart_url}/prepare_shipping_rates.json?${query}`, 'POST')
    .then(async (response) => {
      const data = await this.parseJsonOrEmpty(response);
      const prepareAccepted = response.ok || response.status === 202 || response.status === 422;

      if (this.hasShippingRates(data)) {
        this.needEndLoading = true;
        this.handleSuccessResponse(data);
        return;
      }

      if (data?.shipping_rates?.length === 0) {
        await this.fetchCarrierRatesFromStorefront();
        return;
      }

      if (prepareAccepted) {
        this.pollShippingRates(query);
        return;
      }

      await this.fetchCarrierRatesFromStorefront();
    })
    .catch(() => this.fetchCarrierRatesFromStorefront())
    .finally(() => {
      this.endLoading();
    });
  }

  pollShippingRates(query) {
    this.cartFetch(`${routes.cart_url}/async_shipping_rates.json?${query}`)
    .then(async (response) => this.parseJsonOrEmpty(response))
    .then(async (data) => {
      if (this.hasShippingRates(data)) {
        this.needEndLoading = true;
        this.handleSuccessResponse(data);
        return;
      }

      if (data == null && this.generatedShipping < 8) {
        this.generatedShipping++;
        this.needEndLoading = false;
        setTimeout(() => this.pollShippingRates(query), 1500);
        return;
      }

      await this.fetchCarrierRatesFromStorefront();
    })
    .catch(() => this.fetchCarrierRatesFromStorefront())
    .finally(() => {
      this.endLoading();
    });
  }

  emptyRatesResponse() {
    return { shipping_rates: [] };
  }

  async fetchCarrierRatesFromStorefront() {
    try {
      const rates = await this.queryStorefrontCarrierRates();
      this.needEndLoading = true;
      if (rates.length) {
        this.handleSuccessResponse({ shipping_rates: rates });
      } else {
        this.handleSuccessResponse(this.emptyRatesResponse());
      }
    } catch (e) {
      this.needEndLoading = true;
      this.handleSuccessResponse(this.emptyRatesResponse());
    }
  }

  storefrontEndpoint() {
    const shop = this.dataset.shopDomain || window.Shopify?.shop;
    return `https://${shop}/api/2025-04/graphql.json`;
  }

  async storefrontFetch(query, variables = {}) {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'multipart/mixed;deferSpec=20220824, application/json'
    };
    if (this.dataset.storefrontToken) {
      headers['X-Shopify-Storefront-Access-Token'] = this.dataset.storefrontToken;
    }

    const response = await fetch(this.storefrontEndpoint(), {
      method: 'POST',
      headers,
      body: JSON.stringify({ query, variables })
    });

    return this.parseStorefrontResponse(response);
  }

  async parseStorefrontResponse(response) {
    const contentType = response.headers.get('content-type') || '';
    const text = await response.text();
    const payloads = [];

    if (contentType.includes('application/json')) {
      payloads.push(JSON.parse(text));
    } else {
      text.split(/--graphql/).forEach((part) => {
        const jsonStart = part.indexOf('{');
        if (jsonStart === -1) return;
        try {
          payloads.push(JSON.parse(part.slice(jsonStart).trim()));
        } catch (e) {}
      });
    }

    let data = {};
    payloads.forEach((payload) => {
      if (payload.errors?.length) throw new Error(payload.errors[0].message);
      if (payload.data) data = this.mergeDeep(data, payload.data);
      (payload.incremental || []).forEach((increment) => {
        let target = data;
        (increment.path || []).forEach((key) => {
          if (target[key] == null) target[key] = {};
          target = target[key];
        });
        Object.assign(target, increment.data || {});
      });
    });

    return data;
  }

  mergeDeep(target, source) {
    const output = { ...target };
    Object.keys(source || {}).forEach((key) => {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        output[key] = this.mergeDeep(target[key] || {}, source[key]);
      } else {
        output[key] = source[key];
      }
    });
    return output;
  }

  deliveryOptionsFromCart(cart) {
    const groups = cart?.deliveryGroups;
    const nodes = groups?.nodes || (groups?.edges || []).map((edge) => edge.node);
    return (nodes || []).flatMap((node) => node?.deliveryOptions || []);
  }

  mapDeliveryOptions(options) {
    return options
      .filter((option) => option?.title && option?.estimatedCost?.amount != null)
      .map((option) => ({
        presentment_name: option.title,
        price: option.estimatedCost.amount,
        delivery_days: []
      }));
  }

  async queryStorefrontCarrierRates() {
    const cartResponse = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
    const cart = await cartResponse.json();
    if (!cart.items?.length) return [];

    const address = this.getAddress();
    const countryCode = this.countryIso(address.country);
    if (!countryCode) return [];

    const lines = cart.items.map((item) => ({
      merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
      quantity: item.quantity
    }));

    const buyerIdentity = {
      countryCode,
      deliveryAddressPreferences: [{
        deliveryAddress: {
          firstName: 'Shipping',
          lastName: 'Estimate',
          address1: address.address1,
          city: address.city,
          countryCode,
          zip: address.zip,
          provinceCode: this.provinceCode(countryCode, address.province) || undefined
        }
      }]
    };

    const created = await this.storefrontFetch(`
      mutation EstimateCartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart { id }
          userErrors { message }
        }
      }
    `, { input: { lines, buyerIdentity } });

    const userErrors = created?.cartCreate?.userErrors || [];
    if (userErrors.length) throw new Error(userErrors[0].message);

    const cartId = created?.cartCreate?.cart?.id;
    if (!cartId) return [];

    const ratesQuery = `
      query EstimateCartRates($cartId: ID!) {
        cart(id: $cartId) {
          ... DeliveryGroups @defer
        }
      }
      fragment DeliveryGroups on Cart {
        deliveryGroups(first: 10, withCarrierRates: true) {
          nodes {
            deliveryOptions {
              title
              estimatedCost {
                amount
                currencyCode
              }
            }
          }
        }
      }
    `;

    for (let attempt = 0; attempt < 4; attempt++) {
      const data = await this.storefrontFetch(ratesQuery, { cartId });
      const mapped = this.mapDeliveryOptions(this.deliveryOptionsFromCart(data?.cart));
      if (mapped.length) return mapped;
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }

    return [];
  }
}

customElements.define('shipping-calculator', ShippingCalculator);
}
